import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { SelectButton } from 'primereact/selectbutton';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import { useTranslation } from 'react-i18next';

import Layout from '../../Layout/Layout';
import { useCustomToast } from '../../Contexts/ToastContext';
import FeatureLocked from '../../SharedComponents/FeatureLocked';
import {
  useGetSpinSettings,
  useGetSpinPrizes,
  useGetSpinStats,
  useGetSpinResults,
  useUpdateSpinSettings,
  useAddSpinPrize,
  useUpdateSpinPrize,
  useDeleteSpinPrize,
  useVerifySpinCode,
  useRedeemSpinCode,
} from '../../../queries/spinWin';
import { useInHub } from '../../../nav/useInHub';

/** Pull a SP-XXXX code out of a scanned QR (redeem URL) or raw text. */
function extractCode(text) {
  if (!text) return '';
  const t = String(text).trim();
  const fromUrl = t.match(/redeem\/([^/?#\s]+)/i);
  if (fromUrl) return decodeURIComponent(fromUrl[1]).toUpperCase();
  const sp = t.match(/SP-?[A-Z0-9]{4}/i);
  if (sp) return sp[0].toUpperCase().replace(/^SP-?/, 'SP-');
  return t.toUpperCase();
}

function relTime(iso, t) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.round(diffMs / 60000);
  if (m < 1) return t('spin_win.just_now');
  if (m < 60) return t('spin_win.minutes_ago', { count: m });
  const h = Math.round(m / 60);
  return t('spin_win.hours_ago', { count: h });
}

function expiresIn(iso, t) {
  if (!iso) return '';
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return t('spin_win.expired_label');
  const m = Math.round(diffMs / 60000);
  if (m < 60) return t('spin_win.expires_minutes', { count: m });
  const h = Math.floor(m / 60);
  return t('spin_win.expires_hours_minutes', { h, m: m % 60 });
}

const SWATCHES = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#22C55E', '#E91E63'];
const EMOJIS   = ['🎁', '☕', '🏷️', '🍰', '🥤', '🍕', '💸', '⭐', '🎟️', '😅', '🎉', '👑'];

/**
 * Spin & Win — viral QR prize wheel dashboard.
 *
 *   1. Hero + master Enable switch (pushes/clears the TV idle banner).
 *   2. Today's stats (spins · winners · redeemed).
 *   3. Prizes — editable cards with weight bars & colors.
 *   4. Settings — spins/day, code expiry, show winners on TV.
 *   5. QR link — copy + show QR for printing on the screens.
 *   6. Recent results + cashier redeem box.
 */
export default function SpinWin() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const { showToast } = useCustomToast();
  const { prizeCode: deepLinkCode } = useParams(); // /apps/spin-win/redeem/:prizeCode
  const navigate = useNavigate();

  const { data: settingsData, isLoading: loadingSettings, error: settingsError } = useGetSpinSettings();
  const { data: prizesData,   isLoading: loadingPrizes }   = useGetSpinPrizes();
  const { data: statsData }   = useGetSpinStats();

  const [resultFilter, setResultFilter] = useState('today');
  const { data: resultsData } = useGetSpinResults({ period: resultFilter });

  const updateSettings = useUpdateSpinSettings();
  const addPrize       = useAddSpinPrize();
  const updatePrize    = useUpdateSpinPrize();
  const deletePrize    = useDeleteSpinPrize();
  const verifyCode     = useVerifySpinCode();
  const redeemCode     = useRedeemSpinCode();

  const wheel   = settingsData?.wheel ?? null;
  const spinUrl = settingsData?.spin_url ?? wheel?.qr_url ?? '';
  const prizes  = prizesData?.prizes ?? settingsData?.prizes ?? [];
  const stats   = statsData?.stats ?? {};
  const results = resultsData?.results ?? [];

  const totalWeight = useMemo(
    () => prizes.reduce((s, p) => s + (Number(p.weight) || 0), 0) || 1,
    [prizes]
  );

  const [prizeDialog, setPrizeDialog] = useState(null); // null | {} (new) | prize (edit)
  const [qrOpen, setQrOpen] = useState(false);
  const [redeemInput, setRedeemInput] = useState('');

  // Cashier redeem flow: scan/enter → verify (read-only) → confirm → redeem.
  const [scannerOpen, setScannerOpen] = useState(false);
  const [verifyState, setVerifyState] = useState(null); // null | {loading} | verify body | {redeemed}

  const startVerify = (rawCode) => {
    const code = extractCode(rawCode);
    if (!code) return;
    setVerifyState({ loading: true, prize_code: code });
    verifyCode.mutate(code, {
      onSuccess: (body) => setVerifyState({ ...body, prize_code: body?.prize_code || code }),
      onError: () => setVerifyState({ status: 0, valid: false, msg: t('spin_win.verification_failed'), prize_code: code }),
    });
  };

  const confirmRedeem = () => {
    const code = verifyState?.prize_code;
    if (!code) return;
    redeemCode.mutate(code, {
      onSuccess: (body) => setVerifyState({ ...body, prize_code: code, redeemed_now: body?.status === 200 }),
    });
  };

  const closeVerify = () => {
    setVerifyState(null);
    // Drop the deep-link param so a refresh doesn't re-open the dialog.
    if (deepLinkCode) navigate('/apps/spin-win', { replace: true });
  };

  const onScan = (decodedText) => {
    setScannerOpen(false);
    startVerify(decodedText);
  };

  // Deep link: /apps/spin-win/redeem/:prizeCode auto-opens the verify dialog.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkCode && !deepLinkHandled.current) {
      deepLinkHandled.current = true;
      startVerify(deepLinkCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkCode]);

  const patchSetting = (patch) => updateSettings.mutate({ ...settingsPayload(wheel), ...patch });

  const settingsPayload = (w) => ({
    enabled: !!w?.enabled,
    title: w?.title,
    title_en: w?.title_en,
    max_spins_per_day: w?.max_spins_per_day,
    prize_code_expiry_minutes: w?.prize_code_expiry_minutes,
    show_winners_on_tv: !!w?.show_winners_on_tv,
    display_mode: w?.display_mode || 'bar',
    // Abuse limits. 0 = unlimited on both; max_prizes_per_day is nullable
    // server-side and null behaves identically to 0, so the UI only ever
    // needs the one "0 means off" rule.
    max_wins_per_phone_per_day: w?.max_wins_per_phone_per_day ?? 1,
    max_prizes_per_day: w?.max_prizes_per_day ?? 0,
  });

  const displayModeOptions = [
    { label: `📺 ${t('spin_win.display_mode_bar')}`, value: 'bar' },
    { label: `🖥️ ${t('spin_win.display_mode_full_screen')}`, value: 'full_screen' },
  ];

  const copyLink = () => {
    if (!spinUrl) return;
    navigator.clipboard?.writeText(spinUrl);
    showToast('success', t('spin_win.title'), t('spin_win.link_copied'));
  };

  const confirmDeletePrize = (prize) =>
    confirmDialog({
      message: t('spin_win.confirm_remove_prize', { name: prize.name }),
      header: t('spin_win.remove_prize'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => deletePrize.mutate(prize.id),
    });

  if (settingsError?.response?.status === 402) {
    return (
      <Layout>
        <FeatureLocked feature="spin_and_win" message={settingsError.response.data?.message} />
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmDialog />
      <div className="container-fluid py-3" style={{ maxWidth: 1100 }}>
        {/* ── Hero ─────────────────────────────────────────── */}
        <div className="pp-card p-4 mb-3 d-flex flex-wrap align-items-center gap-3"
             style={{ background: 'linear-gradient(135deg, #3A0CA3 0%, #7209B7 100%)', color: '#fff' }}>
          <span style={{ fontSize: 44 }}>🎰</span>
          <div className="flex-grow-1">
            {!inHub && <h3 className="mb-1" style={{ fontWeight: 800 }}>{t('spin_win.title')}</h3>}
            <p className="mb-0" style={{ opacity: 0.85 }}>
              {t('spin_win.hero_subtitle')}
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontWeight: 700 }}>{wheel?.enabled ? t('spin_win.enabled') : t('spin_win.disabled')}</span>
            <InputSwitch
              checked={!!wheel?.enabled}
              disabled={loadingSettings || updateSettings.isLoading}
              onChange={(e) => patchSetting({ enabled: e.value })}
            />
          </div>
        </div>

        {/* ── Stats ────────────────────────────────────────── */}
        <div className="row g-3 mb-3">
          {[
            { label: t('spin_win.stat_today_spins'),   value: stats.today_spins,   icon: '🎯' },
            { label: t('spin_win.stat_today_winners'), value: stats.today_winners, icon: '🏆' },
            { label: t('spin_win.stat_total_spins'),   value: stats.total_spins,   icon: '🔄' },
            { label: t('spin_win.stat_total_redeemed'),value: stats.total_redeemed,icon: '✅' },
          ].map((s) => (
            <div key={s.label} className="col-6 col-md-3">
              <div className="pp-card p-3 h-100 text-center">
                <div style={{ fontSize: 24 }}>{s.icon}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--pp-text-primary)' }}>
                  {s.value ?? 0}
                </div>
                <div className="small text-muted">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-3">
          {/* ── Prizes ─────────────────────────────────────── */}
          <div className="col-12 col-lg-7">
            <div className="pp-card p-4 h-100">
              <div className="d-flex align-items-center mb-3">
                <h5 className="mb-0 flex-grow-1" style={{ fontWeight: 700 }}>🎁 {t('spin_win.prizes_title')}</h5>
                <Button label={t('spin_win.add_prize')} icon="pi pi-plus" size="small"
                        onClick={() => setPrizeDialog({})} />
              </div>

              {loadingPrizes ? (
                <Skeleton height="8rem" />
              ) : prizes.length === 0 ? (
                <p className="text-muted">{t('spin_win.no_prizes')}</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {prizes.map((p) => {
                    const pct = Math.round(((Number(p.weight) || 0) / totalWeight) * 100);
                    return (
                      <div key={p.id} className="d-flex align-items-center gap-3 p-3"
                           style={{ border: '1px solid var(--pp-border, #e9ecef)', borderRadius: 12, background: 'var(--pp-surface, #fff)' }}>
                        <span style={{
                          fontSize: 24, flex: '0 0 auto', display: 'inline-flex', width: 46, height: 46,
                          alignItems: 'center', justifyContent: 'center', borderRadius: 10,
                          background: p.color || '#eee',
                        }}>{p.icon || '🎁'}</span>

                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ fontWeight: 600 }} className="text-truncate">{p.name}</span>
                            {p.is_losing && (
                              <span className="badge bg-secondary flex-shrink-0" style={{ fontWeight: 600 }}>{t('spin_win.losing_badge')}</span>
                            )}
                          </div>
                          {p.name_en && <div className="small text-muted text-truncate">{p.name_en}</div>}
                          <div className="d-flex align-items-center gap-2 mt-2">
                            <div style={{ flexGrow: 1, height: 8, borderRadius: 999, background: 'var(--pp-primary-subtle, #ececff)', overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: p.color || 'var(--pp-primary)' }} />
                            </div>
                            <span className="small flex-shrink-0" style={{ minWidth: 40, textAlign: 'right', fontWeight: 700, color: 'var(--pp-text-primary)' }}>{pct}%</span>
                          </div>
                        </div>

                        <div className="d-flex gap-2 flex-shrink-0">
                          <Button icon="pi pi-pencil" rounded text size="small" aria-label={t('spin_win.edit_prize_aria')}
                                  onClick={() => setPrizeDialog(p)} />
                          <Button icon="pi pi-trash" rounded text severity="danger" size="small" aria-label={t('spin_win.delete_prize_aria')}
                                  onClick={() => confirmDeletePrize(p)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Settings + QR ──────────────────────────────── */}
          <div className="col-12 col-lg-5">
            <div className="pp-card p-4 mb-3">
              <h5 className="mb-3" style={{ fontWeight: 700 }}>⚙️ {t('spin_win.settings_title')}</h5>
              {loadingSettings ? <Skeleton height="6rem" /> : (
                <div className="d-flex flex-column gap-3">
                  <label className="d-flex align-items-center justify-content-between gap-2">
                    <span className="small">{t('spin_win.max_spins_per_day')}</span>
                    <InputNumber value={wheel?.max_spins_per_day ?? 1} min={1} max={100}
                                 showButtons buttonLayout="horizontal" style={{ width: 130 }}
                                 onValueChange={(e) => patchSetting({ max_spins_per_day: e.value })}
                                 decrementButtonClassName="p-button-secondary"
                                 incrementButtonClassName="p-button-secondary"
                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus" />
                  </label>
                  <label className="d-flex align-items-center justify-content-between gap-2">
                    <span className="small">{t('spin_win.prize_code_expiry')}</span>
                    <InputNumber value={wheel?.prize_code_expiry_minutes ?? 60} min={5} max={1440}
                                 showButtons buttonLayout="horizontal" step={5} style={{ width: 130 }}
                                 onValueChange={(e) => patchSetting({ prize_code_expiry_minutes: e.value })}
                                 decrementButtonClassName="p-button-secondary"
                                 incrementButtonClassName="p-button-secondary"
                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus" />
                  </label>
                  <label className="d-flex align-items-center justify-content-between gap-2">
                    <span className="small">{t('spin_win.show_winners_on_tv')}</span>
                    <InputSwitch checked={!!wheel?.show_winners_on_tv}
                                 onChange={(e) => patchSetting({ show_winners_on_tv: e.value })} />
                  </label>
                  <label className="d-flex flex-column gap-2">
                    <span className="small">{t('spin_win.display_mode_title')}</span>
                    <SelectButton value={wheel?.display_mode || 'bar'}
                                  options={displayModeOptions}
                                  onChange={(e) => e.value && patchSetting({ display_mode: e.value })} />
                  </label>

                  {/* Abuse limits — the per-device spin cap above resets when a
                      guest clears site data or borrows a phone; these two don't. */}
                  <hr className="my-1" />
                  <div className="small" style={{ fontWeight: 700 }}>🛡️ {t('spin_win.limits_title')}</div>

                  <label className="d-flex align-items-center justify-content-between gap-2">
                    <span className="small">{t('spin_win.max_wins_per_phone')}</span>
                    <InputNumber value={wheel?.max_wins_per_phone_per_day ?? 1} min={0} max={100}
                                 showButtons buttonLayout="horizontal" style={{ width: 130 }}
                                 onValueChange={(e) => patchSetting({ max_wins_per_phone_per_day: e.value ?? 0 })}
                                 decrementButtonClassName="p-button-secondary"
                                 incrementButtonClassName="p-button-secondary"
                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus" />
                  </label>

                  <label className="d-flex align-items-center justify-content-between gap-2">
                    <span className="small">{t('spin_win.max_prizes_per_day')}</span>
                    <InputNumber value={wheel?.max_prizes_per_day ?? 0} min={0} max={100000}
                                 showButtons buttonLayout="horizontal" style={{ width: 130 }}
                                 onValueChange={(e) => patchSetting({ max_prizes_per_day: e.value ?? 0 })}
                                 decrementButtonClassName="p-button-secondary"
                                 incrementButtonClassName="p-button-secondary"
                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus" />
                  </label>

                  <p className="small text-muted mb-0">{t('spin_win.limits_hint')}</p>
                </div>
              )}
            </div>

            <div className="pp-card p-4">
              <h5 className="mb-3" style={{ fontWeight: 700 }}>🔗 {t('spin_win.qr_link_title')}</h5>
              <div className="d-flex align-items-center gap-2 mb-3">
                <InputText value={spinUrl} readOnly className="flex-grow-1" style={{ fontSize: 12 }} />
                <Button icon="pi pi-copy" onClick={copyLink} tooltip={t('spin_win.copy_tooltip')} />
                <Button icon="pi pi-qrcode" severity="secondary" onClick={() => setQrOpen(true)} tooltip={t('spin_win.show_qr_tooltip')} />
              </div>
              <p className="small text-muted mb-0">
                {t('spin_win.qr_hint')}
              </p>
            </div>
          </div>
        </div>

        {/* ── Redeem + Results ─────────────────────────────── */}
        <div className="pp-card p-4 mt-3">
          <h5 className="mb-3" style={{ fontWeight: 700 }}>💰 {t('spin_win.redeem_title')}</h5>
          <div className="mb-4" style={{ maxWidth: 460 }}>
            <Button label={t('spin_win.scan_qr_code')} icon="pi pi-camera" className="w-100 mb-3" size="large"
                    onClick={() => setScannerOpen(true)} />
            <div className="text-center text-muted small mb-2">{t('spin_win.or_divider')}</div>
            <div className="d-flex align-items-center gap-2">
              <InputText value={redeemInput} placeholder="SP-XXXX"
                         onChange={(e) => setRedeemInput(e.target.value.toUpperCase())}
                         onKeyDown={(e) => { if (e.key === 'Enter' && redeemInput.trim()) { startVerify(redeemInput.trim()); setRedeemInput(''); } }}
                         className="flex-grow-1" />
              <Button label={t('spin_win.redeem_btn')} icon="pi pi-check"
                      disabled={!redeemInput.trim()}
                      onClick={() => { startVerify(redeemInput.trim()); setRedeemInput(''); }} />
            </div>
          </div>

          <div className="d-flex align-items-center mb-2">
            <h5 className="mb-0 flex-grow-1" style={{ fontWeight: 700 }}>📋 {t('spin_win.recent_results')}</h5>
            <div className="btn-group btn-group-sm">
              {['today', 'week', 'month'].map((p) => (
                <button key={p}
                        className={`btn btn-sm ${resultFilter === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setResultFilter(p)}>
                  {t(`spin_win.filter_${p}`)}
                </button>
              ))}
            </div>
          </div>

          {results.length === 0 ? (
            <p className="text-muted mb-0">{t('spin_win.no_spins_period')}</p>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead><tr className="small text-muted">
                  <th>{t('spin_win.col_player')}</th><th>{t('spin_win.col_prize')}</th><th>{t('spin_win.col_code')}</th><th>{t('spin_win.col_status')}</th><th>{t('spin_win.col_when')}</th>
                </tr></thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id}>
                      <td>{r.player_name || '—'}</td>
                      <td>{r.prize_icon} {r.prize_name}</td>
                      <td><code>{r.prize_code || '—'}</code></td>
                      <td>
                        {r.is_losing ? <span className="text-muted">—</span>
                          : r.redeemed ? <span className="badge bg-success">✅ {t('spin_win.result_used')}</span>
                          : r.expired ? <span className="badge bg-secondary">{t('spin_win.result_expired')}</span>
                          : <span className="badge bg-warning text-dark">⏳ {t('spin_win.result_pending')}</span>}
                      </td>
                      <td className="small text-muted">{formatWhen(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── QR dialog ──────────────────────────────────────── */}
      <Dialog header={t('spin_win.qr_dialog_header')} visible={qrOpen} onHide={() => setQrOpen(false)} dismissableMask
              style={{ width: 340, maxWidth: '95vw' }}>
        <div className="text-center p-3">
          {spinUrl ? <QRCodeSVG value={spinUrl} size={260} level="M" includeMargin style={{ maxWidth: '100%', height: 'auto' }} /> : <p>{t('spin_win.no_link_yet')}</p>}
          <p className="small text-muted mt-3 mb-0" style={{ wordBreak: 'break-all' }}>{spinUrl}</p>
        </div>
      </Dialog>

      {/* ── Prize add/edit dialog ──────────────────────────── */}
      <PrizeDialog
        prize={prizeDialog}
        onClose={() => setPrizeDialog(null)}
        onSave={(payload) => {
          if (prizeDialog?.id) {
            updatePrize.mutate({ id: prizeDialog.id, ...payload }, { onSuccess: () => setPrizeDialog(null) });
          } else {
            addPrize.mutate(payload, { onSuccess: () => setPrizeDialog(null) });
          }
        }}
        saving={addPrize.isLoading || updatePrize.isLoading}
      />

      {/* ── QR scanner (cashier camera) ─────────────────────── */}
      <ScannerDialog open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={onScan} />

      {/* ── Verify → confirm redemption ─────────────────────── */}
      <RedeemConfirmDialog
        state={verifyState}
        onClose={closeVerify}
        onConfirm={confirmRedeem}
        confirming={redeemCode.isLoading}
      />
    </Layout>
  );
}

/**
 * Camera QR scanner — custom flow over the low-level `Html5Qrcode`.
 *
 * Why not the self-contained `Html5QrcodeScanner` widget: its "Request Camera
 * Permissions" button did not reliably fire the browser permission prompt
 * inside our dialog. Here WE own a big "Open Camera" button; clicking it calls
 * `Html5Qrcode.getCameras()` directly — a guaranteed user gesture that triggers
 * getUserMedia → the browser Allow/Deny prompt → camera. We then `start()` a
 * specific camera by id (preferring the rear one), which avoids the
 * `facingMode:'environment'` constraint rejecting on a desktop webcam, and we
 * render explicit error text (denied / no camera / unsupported) so the cashier
 * always knows what happened and can fall back to manual entry.
 */
function ScannerDialog({ open, onClose, onScan }) {
  const { t } = useTranslation();
  const elId = 'spin-qr-reader';
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const [status, setStatus] = useState('idle'); // idle | starting | scanning | error
  const [errMsg, setErrMsg] = useState('');

  const stop = () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (!s) return;
    Promise.resolve()
      .then(() => s.stop())          // rejects/throws if not running → ignored
      .catch(() => {})
      .finally(() => { try { s.clear(); } catch (e) { /* noop */ } });
  };

  const startCamera = async () => {
    setErrMsg('');
    setStatus('starting');
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(t('spin_win.camera_unavailable'));
      }
      const scanner = scannerRef.current || new Html5Qrcode(elId, { verbose: false });
      scannerRef.current = scanner;

      // getCameras() requests permission (Allow/Deny) and returns the list.
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) throw new Error(t('spin_win.no_camera_found'));
      const back = cameras.find((c) => /back|rear|environment/i.test(c.label || '')) || cameras[cameras.length - 1];

      await scanner.start(
        back.id,
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText) => {
          if (handledRef.current) return;
          handledRef.current = true;
          stop();
          onScan(decodedText);
        },
        () => {} // per-frame decode failures: ignore
      );
      setStatus('scanning');
    } catch (e) {
      setStatus('error');
      const denied = e && (e.name === 'NotAllowedError' || /denied|permission|notallowed/i.test(String(e.message || e)));
      setErrMsg(denied
        ? t('spin_win.camera_permission_blocked')
        : ((e && e.message) || t('spin_win.camera_start_failed')));
    }
  };

  const handleShow = () => { handledRef.current = false; setStatus('idle'); setErrMsg(''); };
  const handleHide = () => { stop(); setStatus('idle'); setErrMsg(''); onClose(); };

  // Safety net: stop the camera if the component unmounts while open.
  useEffect(() => () => stop(), []);

  return (
    <Dialog header={t('spin_win.scanner_dialog_header')} visible={open} onShow={handleShow} onHide={handleHide}
            style={{ width: 440, maxWidth: '95vw' }} dismissableMask>
      <div id={elId} style={{ width: '100%', minHeight: status === 'scanning' ? 300 : 0 }} />
      {status !== 'scanning' && (
        <div className="text-center py-3">
          <Button label={status === 'starting' ? t('spin_win.camera_starting') : t('spin_win.open_camera')} icon="pi pi-camera"
                  loading={status === 'starting'} onClick={startCamera} />
          {errMsg
            ? <p className="text-danger small mt-3 mb-0">{errMsg}</p>
            : <p className="text-muted small mt-3 mb-0">{t('spin_win.camera_hint')}</p>}
        </div>
      )}
      {status === 'scanning' && (
        <p className="small text-muted text-center mt-3 mb-0">{t('spin_win.scanning_hint')}</p>
      )}
    </Dialog>
  );
}

/** Verify result + confirm/redeemed states for the cashier. */
function RedeemConfirmDialog({ state, onClose, onConfirm, confirming }) {
  const { t } = useTranslation();
  const open = state != null;
  const loading = !!state?.loading;
  const redeemedNow = !!state?.redeemed_now;
  const valid = state?.valid === true && !redeemedNow;

  let icon = '⏳', tone = 'var(--pp-text-primary)', heading = t('spin_win.checking');
  if (redeemedNow) { icon = '✅'; tone = '#16a34a'; heading = t('spin_win.prize_redeemed_heading'); }
  else if (loading) { icon = '⏳'; heading = t('spin_win.checking'); }
  else if (valid) { icon = '✅'; tone = '#16a34a'; heading = t('spin_win.valid_prize_heading'); }
  else if (state?.is_redeemed) { icon = '⚠️'; tone = '#d97706'; heading = t('spin_win.already_redeemed_heading'); }
  else if (state?.is_expired) { icon = '⛔'; tone = '#dc2626'; heading = t('spin_win.prize_expired_heading'); }
  else if (state) { icon = '❌'; tone = '#dc2626'; heading = state?.status === 404 ? t('spin_win.code_not_found_heading') : t('spin_win.invalid_code_heading'); }

  return (
    <Dialog header={t('spin_win.redeem_dialog_header')} visible={open} onHide={onClose}
            style={{ width: 420, maxWidth: '95vw' }} dismissableMask>
      <div className="text-center pt-2">
        <div style={{ fontSize: 52 }}>{icon}</div>
        <h4 style={{ color: tone, fontWeight: 800 }} className="mb-3">{heading}</h4>

        {loading ? (
          <Skeleton height="6rem" />
        ) : redeemedNow ? (
          <>
            <p className="mb-1" style={{ fontSize: 18 }}>{state.prize_icon} <strong>{state.prize_name}</strong></p>
            <p className="text-muted">{t('spin_win.apply_discount_pos')}</p>
            <p className="text-muted" dir="rtl">{t('spin_win.apply_discount_pos_ar')}</p>
            <Button label={t('spin_win.done_btn')} icon="pi pi-check" className="mt-2" onClick={onClose} />
          </>
        ) : (
          <>
            {(state?.prize_name || state?.player_name) && (
              <div className="text-start mx-auto" style={{ maxWidth: 280 }}>
                {state?.prize_name && <p className="mb-2">🏷️ <strong>{state.prize_name}</strong></p>}
                {state?.player_name && <p className="mb-2">👤 {state.player_name}</p>}
                {state?.won_at && <p className="mb-2 text-muted">⏰ {t('spin_win.won_label')}: {relTime(state.won_at, t)}</p>}
                {state?.expires_at && (
                  <p className="mb-2 text-muted">📅 {state.is_expired ? t('spin_win.expired_label') : `${t('spin_win.expires_in_label')}: ${expiresIn(state.expires_at, t)}`}</p>
                )}
                {state?.is_redeemed && state?.redeemed_at && (
                  <p className="mb-2" style={{ color: '#d97706' }}>{t('spin_win.already_redeemed_at', { time: relTime(state.redeemed_at, t) })}</p>
                )}
              </div>
            )}
            {!valid && state?.msg && <p className="text-muted mt-2">{state.msg}</p>}
            <code className="d-block text-muted mt-2">{state?.prize_code}</code>

            <div className="d-flex justify-content-center gap-2 mt-4">
              {valid ? (
                <>
                  <Button label={t('spin_win.confirm_redemption_btn')} icon="pi pi-check" loading={confirming} onClick={onConfirm} />
                  <Button label={t('spin_win.cancel_btn')} text severity="secondary" onClick={onClose} />
                </>
              ) : (
                <Button label={t('spin_win.close_btn')} severity="secondary" onClick={onClose} />
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

function PrizeDialog({ prize, onClose, onSave, saving }) {
  const { t } = useTranslation();
  const open = prize != null;
  const [form, setForm] = useState({});

  // Re-seed the form whenever a different prize (or "new") is opened.
  React.useEffect(() => {
    if (prize != null) {
      setForm({
        name: prize.name ?? '',
        name_en: prize.name_en ?? '',
        weight: prize.weight ?? 10,
        color: prize.color ?? SWATCHES[0],
        icon: prize.icon ?? '🎁',
        is_losing: !!prize.is_losing,
      });
    }
  }, [prize]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <Dialog header={prize?.id ? t('spin_win.edit_prize_dialog_header') : t('spin_win.add_prize_dialog_header')} visible={open} onHide={onClose}
            style={{ width: 420, maxWidth: '95vw' }} dismissableMask>
      <div className="d-flex flex-column gap-3 pt-2">
        <div>
          <label className="small text-muted d-block mb-1">{t('spin_win.prize_name_ar_label')}</label>
          <InputText value={form.name ?? ''} className="w-100"
                     onChange={(e) => set({ name: e.target.value })} placeholder={t('spin_win.prize_name_ar_ph')} />
        </div>
        <div>
          <label className="small text-muted d-block mb-1">{t('spin_win.prize_name_en_label')}</label>
          <InputText value={form.name_en ?? ''} className="w-100"
                     onChange={(e) => set({ name_en: e.target.value })} placeholder={t('spin_win.prize_name_en_ph')} />
        </div>
        <div>
          <label className="small text-muted d-block mb-1">{t('spin_win.weight_label')}</label>
          <InputNumber value={form.weight ?? 10} min={0} max={1000} className="w-100"
                       onValueChange={(e) => set({ weight: e.value })} />
        </div>
        <div>
          <label className="small text-muted d-block mb-1">{t('spin_win.icon_label')}</label>
          <div className="d-flex flex-wrap gap-2">
            {EMOJIS.map((em) => (
              <button key={em} type="button"
                      className={`pp-swatch d-inline-flex align-items-center justify-content-center${form.icon === em ? ' is-selected' : ''}`}
                      style={{ fontSize: 20, width: 44, height: 44, padding: 0 }}
                      onClick={() => set({ icon: em })}>{em}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="small text-muted d-block mb-1">{t('spin_win.color_label')}</label>
          <div className="d-flex flex-wrap gap-2">
            {SWATCHES.map((c) => (
              <button key={c} type="button" aria-label={c}
                      onClick={() => set({ color: c })}
                      className={`pp-swatch pp-swatch--circle${form.color === c ? ' is-selected' : ''}`}
                      style={{ width: 34, height: 34, background: c }} />
            ))}
          </div>
        </div>
        <label className="d-flex align-items-center gap-2">
          <Checkbox checked={!!form.is_losing} onChange={(e) => set({ is_losing: e.checked })} />
          <span className="small">{t('spin_win.losing_segment_label')}</span>
        </label>

        <div className="d-flex justify-content-end gap-2 pt-2">
          <Button label={t('spin_win.cancel_btn')} text onClick={onClose} />
          <Button label={t('spin_win.save_btn')} icon="pi pi-check" loading={saving}
                  disabled={!form.name?.trim()}
                  onClick={() => onSave({
                    name: form.name.trim(),
                    name_en: form.name_en?.trim() || null,
                    weight: Number(form.weight) || 0,
                    color: form.color,
                    icon: form.icon,
                    is_losing: !!form.is_losing,
                  })} />
        </div>
      </div>
    </Dialog>
  );
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
}
